import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { 
  Upload, Trash2, Play, Headphones, Calendar, 
  User, Tag, FileText, Loader2, Video, Music, 
  ArrowLeft, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [categories, setCategories] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [speaker, setSpeaker] = useState('');
  const [serviceDate, setServiceDate] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/media?limit=100`, {
        withCredentials: true
      });
      setMedia(response.data);
    } catch (error) {
      console.error('Failed to fetch media:', error);
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchCategories();
      fetchMedia();
    }
  }, [user, fetchCategories, fetchMedia]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    const allowedTypes = ['video/', 'audio/'];
    const isAllowed = allowedTypes.some(type => file.type.startsWith(type));
    
    if (!isAllowed) {
      toast.error('Only audio and video files are allowed');
      return;
    }
    
    setSelectedFile(file);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }
    
    if (!title || !category || !serviceDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    setUploading(true);
    
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    const params = new URLSearchParams({
      title,
      description,
      category,
      speaker,
      service_date: format(serviceDate, 'yyyy-MM-dd')
    });

    try {
      await axios.post(`${API_URL}/api/media/upload?${params.toString()}`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Media uploaded successfully!');
      
      // Reset form
      setTitle('');
      setDescription('');
      setCategory('');
      setSpeaker('');
      setServiceDate(null);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Refresh media list
      fetchMedia();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload media');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (mediaId) => {
    try {
      await axios.delete(`${API_URL}/api/media/${mediaId}`, {
        withCredentials: true
      });
      toast.success('Media deleted successfully');
      fetchMedia();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete media');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted">
      <Header />
      
      <main className="py-8 md:py-12">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/')}
                className="mb-2"
                data-testid="back-to-library"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Library
              </Button>
              <h1 className="font-serif text-3xl md:text-4xl font-semibold text-foreground">
                Admin Dashboard
              </h1>
              <p className="text-muted-foreground mt-1">
                Manage worship services and messages
              </p>
            </div>
          </div>

          <Tabs defaultValue="upload" className="space-y-6">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="upload" data-testid="tab-upload">
                <Upload className="w-4 h-4 mr-2" />
                Upload Media
              </TabsTrigger>
              <TabsTrigger value="manage" data-testid="tab-manage">
                <FileText className="w-4 h-4 mr-2" />
                Manage Media
              </TabsTrigger>
            </TabsList>

            {/* Upload Tab */}
            <TabsContent value="upload">
              <Card>
                <CardHeader>
                  <CardTitle className="font-serif">Upload New Media</CardTitle>
                  <CardDescription>
                    Upload audio or video files of worship services and messages
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleUpload} className="space-y-6">
                    {/* File Upload */}
                    <div className="space-y-2">
                      <Label>Media File *</Label>
                      <div
                        className={`upload-dropzone rounded-sm p-8 text-center cursor-pointer ${dragActive ? 'dragging' : ''}`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="file-dropzone"
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="audio/*,video/*"
                          onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                          className="hidden"
                          data-testid="file-input"
                        />
                        {selectedFile ? (
                          <div className="space-y-2">
                            <div className="w-12 h-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                              {selectedFile.type.startsWith('video/') ? (
                                <Video className="w-6 h-6 text-primary" />
                              ) : (
                                <Music className="w-6 h-6 text-primary" />
                              )}
                            </div>
                            <p className="font-medium text-foreground">{selectedFile.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatFileSize(selectedFile.size)}
                            </p>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFile(null);
                              }}
                              data-testid="clear-file"
                            >
                              Change file
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <Upload className="w-10 h-10 mx-auto text-muted-foreground" />
                            <p className="font-medium text-foreground">
                              Drop your file here or click to browse
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Supports MP3, MP4, WAV, MOV, and other audio/video formats
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Title */}
                    <div className="space-y-2">
                      <Label htmlFor="title">Title *</Label>
                      <Input
                        id="title"
                        placeholder="e.g., Sunday Service - Walking in Faith"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                        data-testid="title-input"
                      />
                    </div>

                    {/* Category & Speaker */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Category *</Label>
                        <Select value={category} onValueChange={setCategory} required>
                          <SelectTrigger data-testid="category-select">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.slug}>
                                {cat.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="speaker">Speaker</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="speaker"
                            placeholder="e.g., Pastor John Smith"
                            value={speaker}
                            onChange={(e) => setSpeaker(e.target.value)}
                            className="pl-10"
                            data-testid="speaker-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Service Date */}
                    <div className="space-y-2">
                      <Label>Service Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal"
                            data-testid="date-picker-trigger"
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            {serviceDate ? format(serviceDate, 'PPP') : 'Select date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarComponent
                            mode="single"
                            selected={serviceDate}
                            onSelect={setServiceDate}
                            initialFocus
                            data-testid="calendar"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        placeholder="Brief description of the message..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={3}
                        data-testid="description-input"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full md:w-auto"
                      disabled={uploading}
                      data-testid="upload-submit-button"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Media
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Manage Tab */}
            <TabsContent value="manage">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-serif">Manage Media</CardTitle>
                    <CardDescription>
                      {media.length} {media.length === 1 ? 'item' : 'items'} in library
                    </CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={fetchMedia}
                    data-testid="refresh-media"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  ) : media.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                      <p className="text-muted-foreground">No media uploaded yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4" data-testid="media-list">
                      {media.map((item) => (
                        <div 
                          key={item.id}
                          className="flex items-center gap-4 p-4 rounded-sm border border-border bg-card hover:bg-muted/50 transition-colors"
                          data-testid={`manage-media-item-${item.id}`}
                        >
                          {/* Thumbnail */}
                          <div className="w-20 h-14 rounded-sm overflow-hidden bg-muted flex-shrink-0">
                            <img 
                              src={item.thumbnail_url} 
                              alt={item.title}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {item.media_type === 'video' ? (
                                <Play className="w-4 h-4 text-muted-foreground" />
                              ) : (
                                <Headphones className="w-4 h-4 text-muted-foreground" />
                              )}
                              <h4 className="font-medium text-foreground truncate">
                                {item.title}
                              </h4>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(item.service_date)}
                              </span>
                              {item.speaker && (
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {item.speaker}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Tag className="w-3 h-3" />
                                {item.category}
                              </span>
                              <span>{formatFileSize(item.file_size)}</span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  data-testid={`delete-button-${item.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Media</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete "{item.title}"? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
                                  <AlertDialogAction 
                                    onClick={() => handleDelete(item.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    data-testid="confirm-delete"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
